// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, {useEffect, useState} from 'react';
import {
    Authenticator,
    Heading,
    Image,
    ThemeProvider,
    Loader,
} from '@aws-amplify/ui-react';
import {Auth} from 'aws-amplify';
import '@cloudscape-design/global-styles/index.css';
import '@aws-amplify/ui-react/styles.css';
import Main from './Main';
import {Box, SpaceBetween} from '@cloudscape-design/components';
import * as awsui from '@cloudscape-design/design-tokens';
import {isUsingSSO} from './Utils/AccountUtils';
import './App.css';

const AppHeader = () => (
    <Box margin={{vertical: 'm', horizontal: 'xl'}} textAlign={'center'}>
        <SpaceBetween size={'xl'}>
            <Image
                alt="ResourceLens icon"
                src="/icons/AWS-Zoom_light-bg.svg"
                objectFit="initial"
                objectPosition="50% 50%"
                backgroundColor="initial"
                height="120px"
                width="120px"
                opacity="100%"
            />
            <Heading level={3}>ResourceLens</Heading>
        </SpaceBetween>
    </Box>
);

const AppFooter = ({isLoading}) => {
    if (!isUsingSSO() || !isLoading) return null;

    return (
        <Box textAlign="center" margin={{vertical: 'm'}}>
            <Loader size="large" />
        </Box>
    );
};

const handleFederatedSignIn = async () => {
    try {
        await Auth.federatedSignIn({
            customProvider: window.amplify.Auth.federatedIdpResource,
        });
    } catch (error) {
        console.error('Federated sign-in failed:', error);
    }
};

export const App = () => {
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (isUsingSSO()) {
            document.body.classList.add('sso-enabled');
            Auth.currentAuthenticatedUser().catch(() => {
                setIsLoading(true);
                handleFederatedSignIn();
            });
        }
    }, []);

    const components = {
        Header() {
            return <AppHeader />;
        },
        Footer() {
            return <AppFooter isLoading={isLoading} />;
        },
    };

    const theme = {
        name: 'theme',
        tokens: {
            fonts: {
                default: {
                    variable: {value: awsui.fontFamilyBase},
                    static: {value: awsui.fontFamilyBase},
                },
            },
            colors: {
                brand: {
                    primary: {
                        10: {value: awsui.colorBackgroundButtonPrimaryDisabled},
                        80: {value: awsui.colorBackgroundButtonPrimaryDefault},
                        90: {value: awsui.colorBackgroundButtonPrimaryHover},
                        100: {value: awsui.colorBackgroundButtonPrimaryActive},
                    },
                },
            },
        },
    };

    return (
        <ThemeProvider theme={theme}>
            <Authenticator components={components} hideSignUp={true}>
                <Main />
            </Authenticator>
        </ThemeProvider>
    );
};
export default App;
